import { computed } from 'vue'
import { useStoreProfile } from '@/stores/storeProfile'

/**
 * Feature flags composable for controlling access to experimental features
 *
 * Features can be enabled via:
 * 1. Environment variable (global): VITE_FEATURE_{NAME}=true
 * 2. Per-user database flag: profiles.feature_flags.{name}_enabled = true
 *
 * Environment variables take precedence (if true, feature is enabled for all).
 * Per-user flags allow admins to enable features for specific test users.
 */
export function useFeatureFlags() {
  const storeProfile = useStoreProfile()

  /**
   * Check if a feature is enabled
   * @param {string} featureName - Feature name (e.g., 'stickers', 'animated_stickers')
   * @returns {boolean}
   */
  const hasFeature = (featureName) => {
    // Check environment variable first (global toggle)
    const envKey = `VITE_FEATURE_${featureName.toUpperCase()}`
    if (import.meta.env[envKey] === 'true') {
      return true
    }

    // Check user-specific override (admin-enabled users)
    const profile = storeProfile.getCurrentUserProfile
    if (profile?.feature_flags?.[`${featureName}_enabled`] === true) {
      return true
    }

    return false
  }

  // Pre-computed flags for common features
  const canUseReactions = computed(() => hasFeature('reactions'))
  const canUseComments = computed(() => hasFeature('comments'))
  const canUseSendTo = computed(() => hasFeature('send_to'))
  const canUseListItemCustomImages = computed(() =>
    hasFeature('list_item_custom_images')
  )
  const canUseTiers = computed(() => hasFeature('tiers'))

  return {
    hasFeature,
    canUseReactions,
    canUseComments,
    canUseSendTo,
    canUseListItemCustomImages,
    canUseTiers
  }
}
